import { useState, useRef, useCallback } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api-client';
import Layout from '../../components/Layout';
import ArticleCard, { markArticleOpened, removeOpenedArticle } from './ArticleCard';
import GenerateArticle from '../generate/GenerateArticle';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Spinner } from '../../components/ui/spinner';
import { useTranslation } from '../../i18n/I18nContext';
import { useToast } from '../../contexts/ToastContext';
import { LoadingBar } from '../../components/ui/loading-bar';
import { FloatingPanel } from '../../components/ui/floating-panel';
import { useBackgroundLoader } from '../../contexts/BackgroundLoaderContext';
import type { ArticleHead } from './types';

const PULL_THRESHOLD = 60;

const ArticleList: FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { articles, articlesLoading: loading, articlesError: error, refreshArticles } = useBackgroundLoader();
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [deletingArticleId, setDeletingArticleId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartYRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshArticles();
    } finally {
      setRefreshing(false);
    }
  }, [refreshArticles]);


  const handleArticleClick = (article: ArticleHead) => {
    if (article.processingStatus !== 'ready') return;
    markArticleOpened(article.articleId);
    navigate(`/articles/${article.articleId}`, { state: { article } });
  };

  const handleDeleteClick = (e: React.MouseEvent, articleId: string) => {
    e.stopPropagation();
    setDeletingArticleId(articleId);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingArticleId) return;
    const id = deletingArticleId;
    setDeletingArticleId(null);
    try {
      await apiClient.delete(`/reader/articles/${id}`);
      removeOpenedArticle(id);
      await refreshArticles();
      showToast(t('article_card.deleted'), 'success');
    } catch (err) {
      showToast(t('article_card.delete_failed'), 'error');
    }
  };

  // Pull-to-refresh touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (refreshing) return;
    const scrollEl = scrollContainerRef.current;
    if (scrollEl && scrollEl.scrollTop <= 0) {
      touchStartYRef.current = e.touches[0].clientY;
    } else {
      touchStartYRef.current = null;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartYRef.current === null || refreshing) return;
    const delta = e.touches[0].clientY - touchStartYRef.current;
    if (delta > 0) {
      setPullDistance(Math.min(delta, PULL_THRESHOLD * 2));
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartYRef.current === null) return;
    if (pullDistance >= PULL_THRESHOLD && !refreshing) {
      handleRefresh();
    }
    setPullDistance(0);
    touchStartYRef.current = null;
  };

  const pullIndicatorVisible = pullDistance > 0 || refreshing;

  return (
    <Layout title="">
      <GenerateArticle
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onGenerated={refreshArticles}
      />
      <div
        ref={scrollContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh indicator */}
        {pullIndicatorVisible && (
          <div className="flex items-center justify-center py-3 text-sm text-muted-foreground">
            {refreshing ? (
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                <span>{t('articles.refreshing')}</span>
              </div>
            ) : pullDistance >= PULL_THRESHOLD ? (
              <span>{t('articles.release_to_refresh')}</span>
            ) : (
              <span>{t('articles.pull_to_refresh')}</span>
            )}
          </div>
        )}

        {loading ? (
          <LoadingBar visible />
        ) : error ? (
          <div className="bg-destructive text-destructive-foreground rounded-md p-6 text-center">
            <p className="mb-3">{error}</p>
            <button
              onClick={() => refreshArticles()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-destructive-foreground text-destructive text-sm font-medium hover:opacity-90 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
              {t('articles.retry')}
            </button>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg font-medium text-foreground mb-1">{t('articles.no_articles')}</p>
            <p className="text-sm">{t('articles.no_articles_description')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {articles.map((article) => (
              <ArticleCard
                key={article.articleId}
                article={article}
                onClick={() => handleArticleClick(article)}
                onDelete={(e) => handleDeleteClick(e, article.articleId)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating generate button — constrained to content width */}
      <FloatingPanel side="end" position="bottom">
        <button
          onClick={() => setShowGenerateDialog(true)}
          className="bg-success text-success-foreground rounded-xl w-12 h-12 flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </FloatingPanel>

      <ConfirmDialog
        open={deletingArticleId !== null}
        title={t('article_card.delete_title')}
        message={t('article_card.delete_message')}
        confirmLabel={t('article_card.delete')}
        cancelLabel={t('article_card.cancel')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingArticleId(null)}
      />
    </Layout>
  );
};

export default ArticleList;
